import { ShimmerLoader, StandardAlert } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import React, { useEffect, FunctionComponent, useState, useCallback } from 'react';
import styled from 'styled-components';

import { useFetch } from '@api/ApiHelper';
import { useCustomRole } from '@api/v4/organizations/custom_permissions';
import { PlanGetResponse } from '@api/v4/resources/plan';

import {
  Brandfolder,
} from '@components/bulk_management/user-settings/resource-sidebar';
import { DialogProvider } from '@components/common/providers/DialogContextProvider';
import {
  GetLimitsUsage,
  GetResourceUsage,
  LimitsApiDataResponse,
  LimitsDataResponseObject,
  PlanUsageInfo,
  ResourceUsageResponse,
} from '@components/limits/customLimitsTypes';
import UsageInfoCard, {
  Usage,
} from '@components/limits/usage_info_card/UsageInfoCard';

import { CustomRoleUsageCard } from './CustomRoleUsageCard';
import { findParentBrandfolderKey } from './helpers';
import { ResourceDetails } from './resource-sidebar';
import './styles/plan-limits.scss';

const acceptedResourceTypes = [
  'brandfolder',
  'brandguide',
  'collection',
  'portal',
  'organization',
];

const PENDING_INVITATIONS = {
  singular: t`pending invitation`,
  plural: t`pending invitations`,
};

const StyledP = styled.p`
  font-size: 14px;
  line-height: 20px;
`;

const createWarningFlag = (info: PlanUsageInfo): string =>
  info.limit - info.current === info.invites + 1 && t`1 left`;

const createDangerFlag = (info: PlanUsageInfo): string =>
  (info.current >= info.limit || info.invites >= info.limit - info.current) &&
  t`0 left`;

const checkPendingInvitationsText = (value: number): string => {
  return value === 1
    ? PENDING_INVITATIONS.singular
    : PENDING_INVITATIONS.plural;
};

export const useGetLimitsUsage = (orgKey: string, resourceType = 'brandfolder'): GetLimitsUsage => {
  const { response: adminLimit, fetch: fetchAdminLimits } =
    useFetch<LimitsApiDataResponse>({
      url: `/api/v4/organizations/${orgKey}/limits/${resourceType === 'collection' ? 'brandfolder' : resourceType}/admin?include=resource`,
      fetchOnMount: false,
    });
  const { response: collabLimit, fetch: fetchCollabLimits } =
    useFetch<LimitsApiDataResponse>({
      url: `/api/v4/organizations/${orgKey}/limits/${resourceType === 'collection' ? 'brandfolder' : resourceType}/collaborator?include=resource`,
      fetchOnMount: false,
    });

  return { adminLimit, collabLimit, fetchAdminLimits, fetchCollabLimits };
};

export const useGetResourceUsage = (
  orgKey: string,
  resourceKey: string,
  parentBrandfolderKey: string | undefined,
  resourceType = 'brandfolder',
): GetResourceUsage => {
  const resourceUrl =
    resourceType === 'portal'
      ? `/api/v4/private/organizations/${orgKey}/portals?fields=current_usage,current_invites`
      : `/api/v4/${resourceType === 'collection' ? 'brandfolder' : resourceType}s/${resourceType === 'collection' ? parentBrandfolderKey : resourceKey}?fields=current_usage,current_invites`;

  const { response: resourceUsage, fetch: fetchResourceUsage } = useFetch<ResourceUsageResponse>({
    url: resourceUrl,
    fetchOnMount: false,
  });

  return { resourceUsage, fetchResourceUsage };
};

interface PlanLimitsProps {
  brandfolders: Brandfolder[];
  orgKey: string;
  organizationAdmin: boolean;
  organizationOwner: boolean;
  reload: boolean;
  resource: ResourceDetails;
  setReload: SetStateDispatch<boolean>;
  setReloadUsers: SetStateDispatch<boolean>;
}

export const PlanLimits: FunctionComponent<PlanLimitsProps> = ({
  brandfolders,
  orgKey = BFG.organization_key,
  organizationAdmin,
  organizationOwner,
  reload,
  resource,
  setReload,
  setReloadUsers,
}) => {
  const currentResourceType = resource.type || BFG.resource?.type || 'organization';
  const currentResourceKey = resource.key || BFG.resource?.key;

  const parentBrandfolderKey =
    currentResourceType === 'collection'
      ? findParentBrandfolderKey(currentResourceKey, brandfolders)
      : undefined;

  const [selectedResourceAdmin, setSelectedResourceAdmin] = useState<Usage>();
  const [selectedResourceCollab, setSelectedResourceCollab] = useState<Usage>();

  const { adminLimit, collabLimit, fetchAdminLimits, fetchCollabLimits } = useGetLimitsUsage(orgKey, resource.type);
  const { resourceUsage, fetchResourceUsage } =
    useGetResourceUsage(orgKey, currentResourceKey, parentBrandfolderKey, currentResourceType);
  const { customRole, loading: customRoleLoading, refreshCustomRole } = useCustomRole(orgKey);

  const { response, error, fetch, loading } = useFetch<PlanGetResponse>({
    url: `/api/v4/${BFG.resource?.type || 'organization'}s/${BFG.resource?.key || orgKey}/plan`,
    fields: [
      'admin_invites',
      'admins',
      'collaborator_invites',
      'collaborators',
      'custom_permission_counts',
      'custom_permission_invites',
      'combined_role_counts',
    ],
  });

  const limitsFeatureOn =
    BFG.hasFeature('resource_level_limit') && organizationAdmin;

  const combinedRoleLimitsFeatureOn = BFG.hasFeature('combined_role_limits_rollout');
  const customPermissionsFeatureOn = BFG.hasFeature('custom_permissions_rollout');

  const planInfoAdmin = limitsFeatureOn
    ? {
      current: selectedResourceAdmin?.current,
      limit: Number(selectedResourceAdmin?.limit),
    }
    : {
      current: response?.data.attributes.admins,
      limit: combinedRoleLimitsFeatureOn
        ? response?.data.attributes.limits.combined_roles
        : response?.data.attributes.limits.admins,
    };

  const planInfoCollaborators = limitsFeatureOn
    ? {
      current: selectedResourceCollab?.current,
      limit: Number(selectedResourceCollab?.limit),
    }
    : {
      current: response?.data.attributes.collaborators,
      limit: combinedRoleLimitsFeatureOn
        ? response?.data.attributes.limits.combined_roles
        : response?.data.attributes.limits.collaborators,
    };

  const adminPendingInvitesCount = limitsFeatureOn
    ? Array.isArray(resourceUsage?.data)
      ? resourceUsage?.data.find((res) => res.id === currentResourceKey)
        ?.attributes?.current_invites?.['admin'] : resourceUsage?.data.attributes?.current_invites?.['admin']
    : response?.data.attributes.admin_invites;

  const collaboratorPendingInvitesCount = limitsFeatureOn
    ? Array.isArray(resourceUsage?.data)
      ? resourceUsage?.data.find((res) => res.id === currentResourceKey)
        ?.attributes?.current_invites?.['collaborator']
      : resourceUsage?.data.attributes?.current_invites?.['collaborator']
    : response?.data.attributes.collaborator_invites;

  const admins: PlanUsageInfo = {
    current: planInfoAdmin.current || 0,
    invites: adminPendingInvitesCount || 0,
    limit: planInfoAdmin.limit,
  };

  const collaborators: PlanUsageInfo = {
    current: planInfoCollaborators.current || 0,
    invites: collaboratorPendingInvitesCount || 0,
    limit: planInfoCollaborators.limit,
  };

  // Create a ref to store the custom role limits loader
  const customRoleLimitsLoader = React.useRef<(() => void) | null>(null);

  const loadLimitsContent = useCallback((): void => {
    fetchAdminLimits();
    fetchCollabLimits();
    fetchResourceUsage();
    // Also refresh custom role limits if available
    if (customRoleLimitsLoader.current) {
      customRoleLimitsLoader.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAdminLimits, fetchCollabLimits, fetchResourceUsage]);

  useEffect(() => {
    if (limitsFeatureOn) {
      loadLimitsContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitsFeatureOn, orgKey, resource]);

  useEffect(() => {
    if (reload) {
      setReload(false);
      fetch();
      if (limitsFeatureOn)
        fetchResourceUsage();
    }
  }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (limitsFeatureOn) {
      const buildResourceUsage = (
        limitType: 'admin' | 'collaborator',
        limitData: LimitsDataResponseObject[] = [],
      ): Usage => ({
        current: Array.isArray(resourceUsage?.data) ?
          resourceUsage?.data.find((res) => res.id === currentResourceKey)
            ?.attributes?.current_usage?.[limitType] : resourceUsage?.data.attributes?.current_usage?.[limitType],
        limit:
          limitData?.find(
            (bf) => (currentResourceType === 'collection' ? parentBrandfolderKey : resource.key) === bf.relationships.resource.data.id
          )?.attributes.value,
      });

      const adminResource = buildResourceUsage('admin', adminLimit?.data);
      const collabResource = buildResourceUsage('collaborator', collabLimit?.data);

      setSelectedResourceAdmin({
        current: adminResource.current,
        limit: adminResource.limit,
      });
      setSelectedResourceCollab({
        current: collabResource.current,
        limit: collabResource.limit,
      });
    }
  }, [resource, adminLimit?.data, collabLimit?.data, resourceUsage?.data, limitsFeatureOn]);

  let planLimitsBody;

  if (error) {
    planLimitsBody = (
      <StandardAlert
        className="plan-limits__warning-message"
        showHeading={false}
      >
        <Trans>We were unable to retrieve information regarding your account's plan limits. Try again.</Trans>
      </StandardAlert>
    );
  } else if (loading || response) {
    planLimitsBody = (
      <>
        {resource.type === 'organization' && (
          <>
            {combinedRoleLimitsFeatureOn && (
              <StyledP>
                {customPermissionsFeatureOn ? (
                  <Trans>Total number of Administrators, Collaborators, and custom roles on this plan:</Trans>
                ) : (
                  <Trans>Total number of Administrators and Collaborators on this plan:</Trans>
                )}
                &nbsp;
                <b>
                  {loading ? (
                    <span className="inline-shimmer"><ShimmerLoader  numLines={1} /></span>
                  ) : (
                    response?.data.attributes.limits.combined_roles || 0
                  )}
                </b>
              </StyledP>
            )}
            <StyledP>
              <Trans>Total usage across all Brandfolder resources:</Trans>&nbsp;
              <b>
                {loading ? (
                  <span className="inline-shimmer"><ShimmerLoader numLines={1} /></span>
                ) : (
                  response?.data.attributes.combined_role_counts || 0
                )}
              </b>
            </StyledP>
          </>
        )}
        <div className="plan-limits__user-limits-wrapper user-limit">
          {loading ? (
            <ShimmerLoader className="card-shimmer-loader" numLines={4} />
          ) : (
            <UsageInfoCard
              customClass="user-limit-card"
              dangerAlert={createDangerFlag(admins)}
              hasSetLimits={limitsFeatureOn}
              icon="Person"
              isEnabled={true}
              orgKey={BFG.organization_key}
              setLimitsAction={loadLimitsContent}
              setLimitsEnabled={resource.type === 'organization' && organizationOwner}
              subtitle={`${admins.invites} ${checkPendingInvitationsText(
                admins.invites
              )}`}
              title={t`Administrators`}
              usage={{
                current: admins.current,
                limit: admins.limit,
              }}
              warningAlert={createWarningFlag(admins)}
            />
          )}
          {/* Custom Role Usage Card */}
          {customPermissionsFeatureOn && (
            (loading || customRoleLoading) ? (
              <ShimmerLoader className="card-shimmer-loader" numLines={4} />
            ) : (
              <CustomRoleUsageCard
                brandfolders={brandfolders}
                className="user-limit-card"
                customRole={customRole}
                customRoleLimitsLoader={customRoleLimitsLoader}
                hasSetLimits={limitsFeatureOn}
                organizationAdmin={organizationAdmin}
                organizationKey={BFG.organization_key}
                organizationOwner={organizationOwner}
                planResponse={response}
                refreshCustomRole={refreshCustomRole}
                resourceKey={resource.key}
                resourceType={resource.type}
                resourceUsage={resourceUsage}
                setLimitsAction={loadLimitsContent}
                setReloadUsers={setReloadUsers}
                viewOnly={!window.location.pathname.includes('/manage-users')}
              />
            )
          )}
          {loading ? (
            <ShimmerLoader className="card-shimmer-loader" numLines={4} />
          ) : (
            <UsageInfoCard
              customClass="user-limit-card"
              dangerAlert={createDangerFlag(collaborators)}
              hasSetLimits={limitsFeatureOn}
              icon="Person"
              isEnabled={true}
              orgKey={BFG.organization_key}
              setLimitsAction={loadLimitsContent}
              setLimitsEnabled={resource.type === 'organization' && organizationOwner}
              subtitle={`${collaborators.invites} ${checkPendingInvitationsText(
                collaborators.invites
              )}`}
              title={t`Collaborators`}
              usage={{
                current: collaborators.current,
                limit: collaborators.limit,
              }}
              warningAlert={createWarningFlag(collaborators)}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <DialogProvider>
      {acceptedResourceTypes.includes(resource.type) &&
        <div className="plan-limits">
          {planLimitsBody}
        </div>}
    </DialogProvider>
  );
};
